import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Grid,
    Typography,
    Toolbar,
    IconButton,
    Tooltip,
    Dialog,
    TextField,
    Switch,
    FormControlLabel,
    Stack,
    Chip, Paper,
    Checkbox
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon, Close as CancelIcon } from "@mui/icons-material";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import BlockIcon from "@mui/icons-material/Block";
import { Setting, ListViewModel, SettingsPageProps } from "../../Types/CommonTypes";
import { useAuth, useSnackbar, useLoading } from "../../Context/Contexts";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import LoadingSection from "../LoadingSection";
import CenteredListCard from "../List/CenteredListCardLayout";
import ListCardHeader from "./ListCardHeader";
import ResponsivePagination from "../ResponsivePagination";
const SettingsPage: React.FC<SettingsPageProps> = ({
    title,
    fetchUrl,
    createOrUpdateUrl,
    deleteUrl,
    codeGenerationDisabled
}) => {
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading } = useLoading();
    const [settings, setSettings] = useState<Setting[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({ page: 0, pageSize: 10, filter: "", filters: [] },`setting_${title}`);
    const [linesLoading, setLinesLoading] = useState(false);
    const [editingSetting, setEditingSetting] = useState<Setting | null>(null);
    const [filter, setFilter] = useState("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const { t } = useTranslation();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                showLoading();
                setLinesLoading(true);  
                const response = await authService.post<ListViewModel>(fetchUrl, query);
                setSettings(response.items);
                setTotalCount(response.count);
            } catch (error) {
                console.error("Error fetching settings:", error);
            }
            finally {
                hideLoading();
                setLinesLoading(false);
            }
        };
        fetchSettings();
    }, [query, fetchUrl, authService, showLoading, hideLoading]);

    const handleSearchSubmit = (event?: React.FormEvent) => {
        event?.preventDefault();
        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }
        if (localFilterProperty?.trim() && filter?.trim()) {
            const newFilter = buildQueryFilter(localFilterProperty, filter);
            const isDuplicate = query.filters?.some(
                (f) => f.property === newFilter.property && f.value === newFilter.value
            );
            if (isDuplicate) return;

            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
        } else {
            setQuery({ ...query, filter: filter, page: 0 });
        }
        setFilter("");
        setFilterProperty("");
    };

    const handleAddSetting = () => setEditingSetting({ code: "", description: "", blocked: false, isCreateNew:true });
    const handleEditSetting = (setting: Setting) => setEditingSetting(setting);
    const handleDeleteSetting = async (setting: Setting) => {
        try {
            showLoading();
            await authService.post(deleteUrl, setting);
            setSettings(settings.filter((s) => s.code !== setting.code));
            showSnackbar(t("dialog.deletedSuccessfully"), "success");
            setFilter("");
            handleSearchSubmit();
        } catch (error) {
            console.error("Error deleting setting:", error);
            showSnackbar(t("dialog.deleteFailed"), "error");
        }
        finally {
            hideLoading();
        }
    };

    const handleSaveSetting = async () => {
        if (!editingSetting) return;
        if (!validateSetting(editingSetting)) return;
        try {
            showLoading();
            const response = await authService.post<Setting>(createOrUpdateUrl, editingSetting);
            setSettings((prev) => prev.map((s) => (s.code === response.code ? response : s)));
            setEditingSetting(null);
            showSnackbar(`${t("dialog.savedSuccessfully")}`, "success");
        } catch (error) {
            console.error("Error saving setting:", error);
            showSnackbar(`${t("dialog.failedToSaveChanges")}`, "error");
        }
        finally {
            hideLoading();
        }
    };

    const validateSetting = (ProductGroupSetting: Setting) => {
        if (!ProductGroupSetting) return;

        if (!ProductGroupSetting.description || ProductGroupSetting.description.trim() === "") {
            showSnackbar(t("dialog.descriptionRequired"), "warning");
            return false;
        }





        return true;

    }

    return (
        <CenteredListCard title={title}>

            <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={5} lg={1}>
                        <TextField
                            select
                            label={t("common.field")}
                            value={filterProperty}
                            onChange={(e) => setFilterProperty(e.target.value)}
                            SelectProps={{ native: true }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        >
                            <option value="">{t("common.default")}</option>
                            <option value="ext_Code">{t("common.code")}</option>
                            <option value="ext_Description">{t("common.description")}</option>
                        </TextField>
                    </Grid>
                    <Grid item xs={7} lg={8}>
                        <TextField
                            label={t("common.search")}
                            variant="outlined"
                            fullWidth
                            onChange={(e) => setFilter(e.target.value)}
                            value={filter}
                            disabled={linesLoading}
                        />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <Button variant="contained" type="submit">
                            {t("common.search")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {query.filters?.map((f, index) => (
                    <Chip
                        key={index}
                        label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                        onDelete={() => {
                            const newFilters = query.filters?.filter((_, i) => i !== index);
                            setQuery({ ...query, filters: newFilters, page: 0 });
                        }}
                        deleteIcon={<DeleteIcon />}
                    />
                ))}
            </Box>

            <Button variant="contained" onClick={handleAddSetting} sx={{ mb: 2 }} color="success">
                {`${t("common.create")}`}
            </Button>

            <LoadingSection loading={linesLoading}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {settings.map((setting,index) => (


                    <Grid item
                        xs={12} 
                         sm={settings.length === 1 ? 12 : settings.length === 2 ? 6 : 6}
                        md={settings.length === 1 ? 12 : settings.length === 2 ? 6 : 4}
                        key={setting.code}>

                        <Grid container spacing={1} sx={{ p: 1, border: '1px solid #ccc', borderRadius: 1, cursor: 'pointer', ':hover': { backgroundColor: '#f0f0f0' } }}>
                            <Grid item xs={10}>
                                <ListCardHeader
                                    icon={<SettingsApplicationsIcon fontSize="small" color="action" />}
                                    title={setting.description || setting.code}
                                    status={setting.blocked
                                        ? {
                                            label: t("common.blocked"),
                                            color: "warning",
                                            icon: <BlockIcon fontSize="small" />,
                                        }
                                        : undefined}
                                    hasEdit={ true}
                                />
                                <Typography variant="body1">
                                    {t("common.code")}: {setting.code}
                                </Typography>
                                <Typography variant="body1">
                                    {t("common.description")}: {setting.description}
                                </Typography>
                              

                            </Grid>

                            <Grid item xs={2}>
                                <Stack spacing={1} direction="row" justifyContent="flex-end">
                                    <Tooltip title={t("common.edit")}>
                                        <IconButton size="small" color="primary" onClick={() => handleEditSetting(setting)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t("common.delete")}>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteSetting(setting)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                  
                ))}
            </Grid>
            </LoadingSection>

            <Dialog
                open={!!editingSetting}
                onClose={() => setEditingSetting(null)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: "8px" } }}
            >
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6">{editingSetting?.code ? (t("common.edit") + " " + t("common.setting")) : (t("common.add") + " " + t("common.setting"))}</Typography>
                    <TextField label={ t("common.code")} fullWidth
                        value={editingSetting?.code || ""}
                        onChange={(e) => setEditingSetting({ ...editingSetting, code: e.target.value })
                        } disabled={!editingSetting?.isCreateNew || codeGenerationDisabled === true}
                        helperText={t("help.optionalCodeGeneration")}
                        sx={{ my: 2 }} />
                    <TextField label={t("common.description")}
                        fullWidth value={editingSetting?.description || ""}
                        onChange={(e) => setEditingSetting({ ...editingSetting, description: e.target.value })}
                        error={!editingSetting?.description || editingSetting?.description.trim() === "" }
                        helperText={!editingSetting?.description || editingSetting?.description.trim() === "" ? t("help.descriptionRequired") : ""}
                        sx={{ my: 2 }} />
                    <FormControlLabel control={<Checkbox checked={editingSetting?.blocked || false} onChange={(e) => setEditingSetting({ ...editingSetting, blocked: e.target.checked })} />} label={t("common.blocked")} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                        <Button onClick={handleSaveSetting} variant="contained" startIcon={<SaveIcon />}>{t("common.save")} </Button>
                        <Button onClick={() => setEditingSetting(null)} variant="outlined" startIcon={<CancelIcon />}>{t("common.cancel")} </Button>
                    </Box>
                </Box>
            </Dialog>

            <ResponsivePagination
                page={query.page}
                pageSize={query.pageSize}
                totalCount={totalCount}
                onPageChange={(newPage) => setQuery({ ...query, page: newPage })}
                sx={{ marginTop: "1rem" }}
            />

        </CenteredListCard>
    );
};

export default SettingsPage;